<script setup lang="ts">
import ProblemHubGroup from '~/components/archetypes/ProblemHubGroup.vue';
import ProblemHubIndex from '~/components/archetypes/ProblemHubIndex.vue';

const problemGroups = {
  start: { id: 'getting-started', title: 'Getting started alongside daily work' },
  practice: { id: 'practice-and-records', title: 'Keeping practice and records aligned' },
  control: { id: 'ongoing-control', title: 'Maintaining control after go-live' },
} as const;

useSeoMeta({
  title: 'SharePoint GMP Quality Systems: Practical GxP Quality — QikSolve',
  description:
    'Explore how SharePoint can support a practical GMP quality system through controlled workflows, records, review, and continual improvement.',
});
</script>

<template>
  <div class="bg-slate-50 text-slate-800">
    <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <header class="max-w-3xl">
        <p class="text-sm font-semibold uppercase text-blue-700">
          GMP quality systems
        </p>
        <h1 class="mt-3 text-3xl font-semibold text-slate-950 sm:text-4xl">
          Practical SharePoint GMP Quality Systems
        </h1>
        <p class="mt-5 text-lg leading-relaxed">
          As a GMP Quality Manager, you need a quality system your team can operate and review while
          handling everyday quality work. Before choosing a digital approach, ask whether the process,
          people, records, and validation responsibilities are clear enough to sustain it.
        </p>
      </header>

      <section
        aria-labelledby="challenges-heading"
        class="mt-14"
      >
        <h2
          id="challenges-heading"
          class="text-2xl font-semibold text-slate-950"
        >
          Common challenges in implementing a GMP quality system
        </h2>
        <ProblemHubIndex
          label="Explore quality-system challenges"
          :items="Object.values(problemGroups)"
          class="mt-7"
        />
        <div class="mt-8">
          <ProblemHubGroup
            :id="problemGroups.start.id"
            :title="problemGroups.start.title"
          >
            <p>
              Quality work does not pause while a new system is being designed. The same people needed
              to explain how a process works may also be reviewing events, approving records, and keeping
              existing work moving. Turning that knowledge into a configured workflow, then agreeing
              what must be tested and documented, takes dedicated time. A useful starting point is one
              process with a clear owner and boundaries, alongside an honest view of who can implement
              and maintain it.
            </p>
            <template #questions>
              <ul class="mt-3 grid gap-2 sm:grid-cols-2">
                <li>
                  <NuxtLink
                    to="/product/quality-system-implementation-capacity"
                    class="flex min-h-11 items-center py-2 font-semibold text-brand-700 underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
                  >
                    Who has capacity to implement it?
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink
                    to="/product/validation-planning-for-sustainable-teams"
                    class="flex min-h-11 items-center py-2 font-semibold text-brand-700 underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
                  >
                    Who will plan and maintain validation?
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </ProblemHubGroup>
          <ProblemHubGroup
            :id="problemGroups.practice.id"
            :title="problemGroups.practice.title"
            shaded
          >
            <p>
              When a decision moves between email, a spreadsheet, and a controlled record, it can be
              hard to see who is expected to act next or which version reflects the current work. Teams
              may keep a process moving through manual updates while the procedure describes a different
              path. Mapping the handoffs, record creation, and review points against what people actually
              do helps expose where administration is building up and where the written instructions
              need to catch up.
            </p>
            <template #questions>
              <ul class="mt-3 grid gap-2 sm:grid-cols-2">
                <li>
                  <NuxtLink
                    to="/product/reducing-quality-system-administration"
                    class="flex min-h-11 items-center py-2 font-semibold text-brand-700 underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
                  >
                    Where is administration accumulating?
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink
                    to="/product/procedures-and-work-instructions-alignment"
                    class="flex min-h-11 items-center py-2 font-semibold text-brand-700 underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
                  >
                    Do procedures match the work?
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </ProblemHubGroup>
          <ProblemHubGroup
            :id="problemGroups.control.id"
            :title="problemGroups.control.title"
          >
            <p>
              A signed validation package marks an important stage, not the end of the quality team's
              responsibility. Roles change, workflows are adjusted, and records continue to be created
              and reviewed after go-live. If no one owns the assessment of those changes, access decisions,
              and the evidence of routine review, it becomes harder to show how the system remains
              controlled in use. Plan for that ongoing ownership before the initial project team moves on.
            </p>
            <template #questions>
              <ul class="mt-3 grid gap-2 sm:grid-cols-2">
                <li>
                  <NuxtLink
                    to="/product/ongoing-control-beyond-validation"
                    class="flex min-h-11 items-center py-2 font-semibold text-brand-700 underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
                  >
                    What happens after validation?
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink
                    to="/product/data-and-system-governance-for-quality-teams"
                    class="flex min-h-11 items-center py-2 font-semibold text-brand-700 underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
                  >
                    Who governs data and access?
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </ProblemHubGroup>
        </div>
      </section>

      <section
        aria-labelledby="readiness-heading"
        class="mt-16 border-t-4 border-brand-700 bg-brand-50 px-4 py-10 sm:px-6 lg:px-8"
      >
        <h2
          id="readiness-heading"
          class="text-2xl font-semibold text-slate-950"
        >
          Check readiness before choosing a pathway
        </h2>
        <p class="mt-3 max-w-3xl leading-relaxed">
          Start with a defined quality process, not a platform choice. Use these questions to identify
          what is known and what still needs agreement.
        </p>
        <ol class="mt-6 grid gap-6 sm:grid-cols-3">
          <li class="border-l-2 border-slate-300 pl-4">
            <h3 class="font-semibold text-slate-950">
              Process and workflow
            </h3>
            <p class="mt-2">
              Which workflow is in scope, where does it begin and end, and which handoffs need to be mapped?
            </p>
          </li>
          <li class="border-l-2 border-slate-300 pl-4">
            <h3 class="font-semibold text-slate-950">
              Ownership and evidence
            </h3>
            <p class="mt-2">
              Who owns decisions, records, approvals, access, and the expected review of quality work?
            </p>
          </li>
          <li class="border-l-2 border-slate-300 pl-4">
            <h3 class="font-semibold text-slate-950">
              Intended use and capacity
            </h3>
            <p class="mt-2">
              What intended use needs assessment and validation, and who can sustain review and change control?
            </p>
          </li>
        </ol>
      </section>

      <section
        aria-labelledby="pathway-heading"
        class="mt-16 border-t-4 border-brand-700 bg-white px-4 py-10 sm:px-6 lg:px-8"
      >
        <h2
          id="pathway-heading"
          class="text-2xl font-semibold text-slate-950"
        >
          Choose the next conversation
        </h2>
        <div class="mt-6 grid gap-8 md:grid-cols-2">
          <div>
            <h3 class="text-lg font-semibold text-slate-950">
              Your process and ownership are defined
            </h3>
            <p class="mt-2 leading-relaxed">
              If you are ready to assess a digital quality-system option, explore
              <a
                href="https://inq.qiksolve.com"
                class="text-blue-700 underline hover:text-blue-900"
              >INQ eQMS product information</a>
              for product-specific detail and to consider fit against your requirements.
            </p>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-slate-950">
              Ownership or handoffs are still unclear
            </h3>
            <p class="mt-2 leading-relaxed">
              Start with the
              <NuxtLink
                to="/explore-by-need/quality-process-mapping-development-and-implementation"
                class="text-blue-700 underline hover:text-blue-900"
              >
                quality process mapping need guide
              </NuxtLink>
              to work through fit, then consider the
              <NuxtLink
                to="/services/quality-process-mapping-development-and-implementation"
                class="text-blue-700 underline hover:text-blue-900"
              >
                Quality Process Mapping, Development and Implementation service
              </NuxtLink>.
            </p>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="related-guidance-heading"
        class="mt-14 border-t border-slate-300 pt-10"
      >
        <h2
          id="related-guidance-heading"
          class="text-2xl font-semibold text-slate-950"
        >
          Related guidance
        </h2>
        <ul class="mt-5 grid gap-5 sm:grid-cols-2">
          <li>
            <NuxtLink
              to="/product/sharepoint-governance"
              class="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              SharePoint governance
            </NuxtLink>
            <p class="mt-1 text-sm leading-relaxed text-slate-700">
              Site structure, permissions, metadata, retention, and access.
            </p>
          </li>
          <li>
            <NuxtLink
              to="/product/regulatory-compliance"
              class="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Regulation to practice
            </NuxtLink>
            <p class="mt-1 text-sm leading-relaxed text-slate-700">
              Interpreting regulatory expectations in an operational context.
            </p>
          </li>
          <li>
            <NuxtLink
              to="/product/quality-dashboards"
              class="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Quality dashboards and reporting
            </NuxtLink>
            <p class="mt-1 text-sm leading-relaxed text-slate-700">
              Reporting information for review and quality decisions.
            </p>
          </li>
          <li>
            <NuxtLink
              to="/services"
              class="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              QikSolve services
            </NuxtLink>
            <p class="mt-1 text-sm leading-relaxed text-slate-700">
              Explore delivery support for process design and implementation.
            </p>
          </li>
          <li>
            <NuxtLink
              to="/explore-by-need/software-implementation-maintenance-and-management"
              class="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Assess software implementation and ongoing management needs
            </NuxtLink>
            <p class="mt-1 text-sm leading-relaxed text-slate-700">
              Work through fit questions before choosing an implementation and support pathway.
            </p>
          </li>
        </ul>
      </section>

      <aside class="mt-10 max-w-3xl border-l-2 border-slate-400 pl-5 leading-relaxed">
        <h2 class="font-semibold text-slate-950">
          Organisational responsibility
        </h2>
        <p class="mt-2 text-sm text-slate-700">
          QikSolve is not a regulatory authority, auditor, or certification body. Your organisation
          remains responsible for its regulatory decisions, controls, approvals, validation, and
          ongoing oversight.
        </p>
      </aside>

      <section
        aria-labelledby="contact-heading"
        class="mt-14 border-t border-slate-300 pt-10"
      >
        <h2
          id="contact-heading"
          class="text-2xl font-semibold text-slate-950"
        >
          Discuss your quality-system context
        </h2>
        <p class="mt-3 max-w-3xl leading-relaxed">
          Bring the workflow, ownership, records, or validation questions your team is working through.
        </p>
        <NuxtLink
          to="/contact"
          class="mt-5 inline-block font-semibold text-blue-700 underline hover:text-blue-900"
        >
          Book a discovery call
        </NuxtLink>
      </section>
    </div>
  </div>
</template>
